import { Component, computed, EventEmitter, input, Output, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { provideLuxonDateAdapter } from '@angular/material-luxon-adapter';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerInputEvent, MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { FaIconLibrary, FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faCalendarAlt } from '@fortawesome/pro-solid-svg-icons';
import { DateTime } from 'luxon';

@Component({
    selector: 'r1-compass-lib-date-picker',
    imports: [FontAwesomeModule, MatFormFieldModule, MatDatepickerModule, MatInputModule, FormsModule, MatButtonModule],
    providers: [provideLuxonDateAdapter()],
    templateUrl: './date-picker.component.html',
    styleUrl: './date-picker.component.scss',
})
export class DatePickerComponent {
    constructor() {
        const library = inject(FaIconLibrary);

        library.addIcons(faCalendarAlt);
    }

    preSelectedDate = input.required<DateTime>();

    selectedDate = computed(() => {
        if (this.preSelectedDate()) {
            return this.preSelectedDate()!
                .set({ hour: 0 })
                .set({ minute: 0 })
                .set({ second: 0 })
                .set({ millisecond: 0 })
                .toISO();
        } else {
            return DateTime.now()
                .set({ hour: 0 })
                .set({ minute: 0 })
                .set({ second: 0 })
                .set({ millisecond: 0 })
                .toISO();
        }
    });

    @Output()
    dateChange: EventEmitter<DateRange> = new EventEmitter<DateRange>();

    onDateChange(event: MatDatepickerInputEvent<string>) {
        const newSelectedDate = DateTime.fromISO(event.value!)
            .set({ hour: 0 })
            .set({ minute: 0 })
            .set({ second: 0 })
            .set({ millisecond: 0 })
            .toISO();
        const newDateRange: DateRange = {
            start: this.getStart(newSelectedDate),
            end: this.getEnd(newSelectedDate),
        };
        this.dateChange.emit(newDateRange);
    }

    onNextDay() {
        const newDateRange: DateRange = {
            start: this.getIncrementedStart(this.selectedDate()!),
            end: this.getIncrementedEnd(this.selectedDate()!),
        };
        this.dateChange.emit(newDateRange);
    }

    onPrevDay() {
        const newDateRange: DateRange = {
            start: this.getDecrementedStart(this.selectedDate()!),
            end: this.getDecrementedEnd(this.selectedDate()!),
        };
        this.dateChange.emit(newDateRange);
    }

    private getStart(date: string | null): string {
        return DateTime.fromISO(date!)
            .set({ hour: 0 })
            .set({ minute: 0 })
            .set({ second: 0 })
            .set({ millisecond: 0 })
            .toISO()!;
    }

    private getIncrementedStart(date: string): string {
        return DateTime.fromISO(date)
            .plus({ days: 1 })
            .set({ hour: 0 })
            .set({ minute: 0 })
            .set({ second: 0 })
            .set({ millisecond: 0 })
            .toISO()!;
    }

    private getIncrementedEnd(date: string): string {
        return DateTime.fromISO(date)
            .plus({ days: 1 })
            .set({ hour: 23 })
            .set({ minute: 59 })
            .set({ second: 59 })
            .set({ millisecond: 999 })
            .toISO()!;
    }

    private getEnd(date: string | null): string {
        return DateTime.fromISO(date!)
            .set({ hour: 23 })
            .set({ minute: 59 })
            .set({ second: 59 })
            .set({ millisecond: 999 })
            .toISO()!;
    }

    private getDecrementedStart(date: string): string {
        return DateTime.fromISO(date)
            .minus({ days: 1 })
            .set({ hour: 0 })
            .set({ minute: 0 })
            .set({ second: 0 })
            .set({ millisecond: 0 })
            .toISO()!;
    }

    private getDecrementedEnd(date: string): string {
        return DateTime.fromISO(date)
            .minus({ days: 1 })
            .set({ hour: 23 })
            .set({ minute: 59 })
            .set({ second: 59 })
            .set({ millisecond: 999 })
            .toISO()!;
    }
}

export type DateRange = {
    start: string;
    end: string;
};
